import { useI18n } from "@/hooks/useI18n";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SEOHead } from "@/components/SEOHead";
import { 
  MapPin, 
  Thermometer, 
  CheckCircle, 
  Award, 
  Mountain,
  Leaf,
  Clock,
  Shield
} from "lucide-react";

export default function Farms() {
  const { t } = useI18n();

  const partnerFarms = [
    {
      name: "Finca Andina",
      location: "Cayambe, 2,850m altitude",
      specialties: ["Premium Roses", "Gypsophila", "Alstroemeria"],
      certifications: ["Agrocalidad", "Fair Trade", "MPS-A"],
      image: "/placeholder.svg",
      description: "High-altitude farm specializing in premium rose varieties with advanced post-harvest facilities."
    },
    {
      name: "Sierra Alta",
      location: "Tabacundo, 3,100m altitude",
      specialties: ["White Roses", "Spray Roses", "Hypericum"],
      certifications: ["Agrocalidad", "Rainforest Alliance", "MPS-A"],
      image: "/placeholder.svg",
      description: "Sustainable farming practices with state-of-the-art cold storage and processing facilities."
    },
    {
      name: "Nevado Bloom",
      location: "Machachi, 2,950m altitude",
      specialties: ["Chrysanthemums", "Carnations", "Mixed Bouquets"],
      certifications: ["Agrocalidad", "Global GAP", "MPS-A"],
      image: "/placeholder.svg",
      description: "Family-owned farm with three generations of flower cultivation expertise and modern infrastructure."
    }
  ];

  const qualityChecklist = [
    {
      category: "Reception Temperature",
      checks: ["0-4°C maintained", "Temperature logging", "Cold chain verification"],
      icon: Thermometer
    },
    {
      category: "Botanical Quality",
      checks: ["Stem length verification", "Flower head assessment", "Foliage condition"],
      icon: Leaf
    },
    {
      category: "Physical Condition",
      checks: ["Damage inspection", "Pest verification", "Structural integrity"],
      icon: Shield
    },
    {
      category: "Post-Harvest",
      checks: ["Hydration levels", "Cut freshness", "Vase life potential"],
      icon: Clock
    }
  ];

  const certifications = [
    { name: "Agrocalidad", description: "Ecuador's official plant health certification" },
    { name: "Chamber of Commerce", description: "Certificate of origin verification" },
    { name: "MPS-A", description: "Environmental certification for sustainable production" },
    { name: "Global GAP", description: "Good Agricultural Practices certification" },
    { name: "Fair Trade", description: "Ethical labor and environmental standards" },
    { name: "Rainforest Alliance", description: "Biodiversity and community protection" }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-andean text-andean-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-display font-light mb-6 tracking-wide">
              {t('page.farms.title')}
            </h1>
            <p className="text-lg md:text-xl mb-8 text-andean-cream/90 font-light leading-relaxed">
              We work exclusively with certified Ecuadorian farms that meet our strict quality standards for premium flower production
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Badge variant="secondary" className="bg-andean-cream/20 text-andean-cream border-andean-cream/30">
                <Mountain className="w-4 h-4 mr-1" />
                High Altitude Growing
              </Badge>
              <Badge variant="secondary" className="bg-andean-cream/20 text-andean-cream border-andean-cream/30">
                <Thermometer className="w-4 h-4 mr-1" />
                Cold Chain Certified
              </Badge>
              <Badge variant="secondary" className="bg-andean-cream/20 text-andean-cream border-andean-cream/30">
                <Award className="w-4 h-4 mr-1" />
                International Standards
              </Badge>
            </div>
          </div>
        </div>
      </section>


      {/* Quality Control */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl font-display font-light text-foreground mb-4 tracking-wide">
              Quality Control Checklist
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Every flower shipment undergoes rigorous quality control at multiple stages
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {qualityChecklist.map((category, index) => (
              <Card key={index} className="text-center h-full">
                <CardHeader>
                  <div className="w-16 h-16 bg-gradient-andean rounded-full flex items-center justify-center mx-auto mb-4">
                    <category.icon className="w-8 h-8 text-andean-cream" />
                  </div>
                  <CardTitle className="text-lg">{category.category}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {category.checks.map((check, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-andean-forest flex-shrink-0" />
                        {check}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl font-display font-light text-foreground mb-4 tracking-wide">
              Certifications & Standards
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Our partner farms hold internationally recognized certifications ensuring quality, sustainability, and ethical practices
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certifications.map((cert, index) => (
              <Card key={index} className="hover:shadow-andean transition-all duration-300">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3 text-lg">
                    <Award className="w-6 h-6 text-andean-forest" />
                    {cert.name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    {cert.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Traceability Section */}
      <section className="py-20 bg-gradient-andean text-andean-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-display font-light mb-6 tracking-wide">
              Complete Traceability
            </h2>
            <p className="text-xl mb-8 text-andean-cream/90">
              Every flower shipment includes complete traceability information from farm to destination
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div className="bg-andean-cream/10 p-6 rounded-lg">
                <CheckCircle className="w-8 h-8 mx-auto mb-3" />
                <h3 className="font-light mb-2 tracking-wide">Lot Tracking</h3>
                <p className="text-sm text-andean-cream/80">
                  Unique lot numbers for every shipment
                </p>
              </div>
              <div className="bg-andean-cream/10 p-6 rounded-lg">
                <Clock className="w-8 h-8 mx-auto mb-3" />
                <h3 className="font-light mb-2 tracking-wide">Cut Date</h3>
                <p className="text-sm text-andean-cream/80">
                  Precise harvest timing documentation
                </p>
              </div>
              <div className="bg-andean-cream/10 p-6 rounded-lg">
                <Thermometer className="w-8 h-8 mx-auto mb-3" />
                <h3 className="font-light mb-2 tracking-wide">Temperature Logs</h3>
                <p className="text-sm text-andean-cream/80">
                  Optional temperature monitoring during transport
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}